import { HeadCell, Order } from "@/types/component/TableTypes";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import { Box, TableCell, Typography, useTheme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableRow from "@mui/material/TableRow";
import RichTooltip from "../common/RichTooltip";

type EnhancedTableProps = {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
  orderBy: string | null;
  rowCount: number;
  headCells: HeadCell[];
  selectionColumn: boolean;
  border?: boolean;
};

export function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, headCells, border } = props;
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const theme = useTheme();

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, count) => (
          <TableCell
            key={"head-cell-key-" + headCell.id}
            data-testid={"head-cell-key-" + headCell.id}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              paddingRight: 2,
              paddingLeft: 2,
              paddingBottom: 2,
              borderRightWidth: 0,
              borderRightColor: "black",
              borderRightStyle: "solid",
              ...theme.applyStyles("dark", {
                backgroundColor: theme.palette.grey[50],
              }),
            })}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              {!headCell.disableOrdering ? (
                <>
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={createSortHandler(headCell.id)}
                    sx={{ width: "100%" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="body1"
                        color="text.primary"
                        fontWeight="500"
                        noWrap={headCell.noWrap}
                      >
                        {headCell.label}
                      </Typography>
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={{ ...visuallyHidden }}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </Box>
                  </TableSortLabel>
                  {headCell.action ? headCell.action : null}
                </>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width={1}
                >
                  <Typography
                    variant="body1"
                    color="text.primary"
                    fontWeight="500"
                    noWrap={headCell.noWrap}
                  >
                    {headCell.label}
                  </Typography>
                  {headCell.action ? headCell.action : null}
                </Box>
              )}
              <Box display="flex" alignItems="center" height={1}>
                {headCell.info ? (
                  <Box
                    display="flex"
                    fontSize="17px"
                    pr="8px"
                    alignItems="center"
                  >
                    <RichTooltip body={<>{headCell.info}</>}>
                      <InfoOutlinedIcon
                        sx={{ color: theme.palette.action.active }}
                        fontSize="inherit"
                      />
                    </RichTooltip>
                  </Box>
                ) : null}

                {border && count != headCells.length - 1 ? (
                  <>
                    <Box paddingLeft={2} />
                    <Box
                      display="flex"
                      width="3px"
                      height="10px"
                      sx={{ borderLeft: 1, borderColor: "divider" }}
                    />
                  </>
                ) : null}
              </Box>
            </Box>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
